"use client";

import { Scale } from "@/animations/scale";
import Image from "@/components/Image";
import { cn } from "@/utils";
import { formatNumber } from "@/utils/number-format";
import { useResize } from "@/utils/useResize";
import { cva } from "class-variance-authority";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { useWallet } from "../apis/wallet";
import { HeaderFinder } from "./header-finder";
import { WrapperFinder } from "./wrapper-finder";
import { ModalQRApp } from "../Menu/modals/ModalQRApp";

const variants = cva("w-full", {
  variants: {
    twSize: {
      xs: "max-w-[80%] ml-[14px] mt-[32px]",
      sm: "max-w-[80%] ml-[14px] mt-[32px]",
      md: "max-w-[80%] ml-[14px] mt-[32px]",
      lg: "max-w-[600px] ml-[32px] mt-[32px]",
      xl: "max-w-[533px] ml-[60px] -mt-[23px]",
      "2xl": "max-w-[566px] ml-[64px] -mt-[24]",
      "3xl": "max-w-[600px] ml-[68px] -mt-[26px]",
    },
  },
  defaultVariants: {
    twSize: "2xl",
  },
});

export function FinderWallet() {
  const { twSize } = useResize();
  const { data } = useWallet();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const cumulative_user = useMemo(() => {
    const value = data?.data?.cumulative_user;
    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));

    let txt: string = `${temp.value}`;
    if (`${temp.value2}`.charAt(0) !== "0") {
      txt = `${txt}.${temp.value2.toString().charAt(0)}`;
    }
    txt = txt + `${temp.text ? temp.text + "+" : ""}`;

    return `${txt}`;
  }, [data?.data]);

  const cumulative_action = useMemo(() => {
    const value = data?.data?.cumulative_action;
    if (!value) {
      return "--";
    }
    const temp = formatNumber(Number(value));
    return `${temp.value}${temp.text ? temp.text + "+" : ""}`;
  }, [data?.data]);

  return (
    <>
      <Scale>
        <WrapperFinder
          className={cn(variants({ twSize: twSize }), "overflow-hidden")}
        >
          <HeaderFinder>
            <p className="text-[#23262F] text-center text-[13px] font-medium leading-4 truncate">
              Holdstation Wallet
            </p>
          </HeaderFinder>

          <div className="wallet-bg">
            <div className="p-5">
              <p className="text-xl md:text-[1.75rem] md:leading-[2.5rem] font-medium">
                <span className="text-[#777E90]">
                  Smart wallet for the community, leveraging AI technology.
                </span>{" "}
                <span className="text-[#141416]">
                  Streamlines DeFi interactions for everyday users.
                </span>
              </p>
            </div>

            <div className="md:h-[296px] flex flex-col md:flex-row-reverse md:items-end gap-4">
              <div className="flex flex-row md:flex-col gap-10 px-5 md:py-5">
                <div className="flex flex-col md:items-end">
                  <p className="text-xl md:text-[2.5rem] leading-8 md:leading-[3rem] font-medium text-black md:text-white">
                    {cumulative_user}
                  </p>
                  <p className="text-xs md:text-base font-medium  text-black md:text-white/60">
                    Total users
                  </p>
                </div>

                <div className="flex flex-col md:items-end">
                  <p className="text-xl md:text-[2.5rem] leading-8 md:leading-[3rem] font-medium text-black md:text-white">
                    {cumulative_action}
                  </p>
                  <p className="text-xs md:text-base font-medium text-black md:text-white/60 whitespace-nowrap">
                    Total onchain actions
                  </p>
                </div>
              </div>
              <div className="flex-1 flex items-end gap-4 pl-4">
                <div className="max-w-[213px]">
                  <Image
                    src={require("@/assets/images/webp/home.png")}
                    className="w-full h-full object-cover"
                    alt="close"
                    width={853}
                    height={1152}
                  />
                </div>

                <div className="max-w-[165px]">
                  <Image
                    src={require("@/assets/images/webp/trading.png")}
                    className="w-full h-full object-cover"
                    alt="close"
                    width={661}
                    height={1048}
                  />
                </div>
              </div>
            </div>

            <div
              className={clsx(
                "grid grid-cols-[40px_1fr_126px] md:grid-cols-[48px_1fr_142px] gap-2.5 lg:gap-4 p-4",
                "p-4 border-t border-white/15 bg-black/10"
              )}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl flex-shrink-0">
                <Image
                  src={require("@/assets/icons/ic_wallet.png")}
                  className="w-full h-full object-cover"
                  alt="close"
                  width={216}
                  height={216}
                />
              </div>

              <div className="flex-1">
                <p className="text-sm font-medium text-white line-clamp-1">
                  Holdstation Wallet
                </p>
                <p className="text-sm font-medium text-white/60 line-clamp-1">
                  4.5/ 5 Ratings on App Store
                </p>
              </div>

              <button
                type="button"
                className={clsx(
                  "md:px-6 md:py-2.5 rounded-full bg-white",
                  "px-4 py-[10px] text-sm leading-5 text-[#141416] font-medium"
                )}
                data-action="download_holdstation_app"
                onClick={() => {
                  setIsOpen(true);
                }}
              >
                Download Now
              </button>
            </div>
          </div>
        </WrapperFinder>
      </Scale>

      {isOpen ? (
        <ModalQRApp
          onClose={() => {
            setIsOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
